/**
 * v10 top-left gold counter.
 *
 * Per the v10 UI spec (`docs/superpowers/specs/2026-05-01-ui-implementation-spec.md`
 * §1) gold is the ONLY HUD currency; every other resource lives in the
 * 6×2 backpack on the main plate. The previous v9 layout had a 4-resource
 * strip in this slot — that strip is gone, replaced by this single counter.
 *
 * Sources:
 *   - `cube.resources.gold` — uint16 server-authoritative counter (still scalar
 *     in `RESOURCE_POLICY` per the 2026-05-03 migration; lumber/stone et al.
 *     became backpack items, gold stayed on the resource bar).
 *   - Visual reference: `docs/design/mockups/hud-v10.html:707-711` (the v9
 *     resources strip collapsed to one cell; the layout copies its panel
 *     chrome since the v10 mood-reference renders only redress the bottom
 *     plate so far).
 *
 * Painted PNG chrome: deferred — methodology doc lists this as a separate
 * surface (`2026-05-02-hud-implementation-methodology.md` §"Per-surface asset
 * inventory"). Today renders with the same flat-dark + gold-rim styling as
 * the rest of the v10 chrome so it doesn't visually clash.
 */
import type { Cube } from "@tribez/shared";

export interface GoldCounter {
  /** Mirror schema state into the counter. Call each frame from main.ts. */
  refresh(cube: Cube): void;
  /** Remove the DOM element. */
  destroy(): void;
}

const COIN_SVG = `<svg viewBox="0 0 20 20" class="gc-icon">
  <circle cx="10" cy="10" r="9" fill="#f7c360" stroke="#8a5f18" stroke-width="1.4"/>
  <text x="10" y="14" text-anchor="middle" font-size="12" font-weight="700"
        fill="#5a3f10" font-family="Cinzel, serif">G</text>
</svg>`;

export function createGoldCounter(): GoldCounter {
  const root = document.createElement("div");
  root.id = "gold-counter";
  root.className = "gc-root";
  root.innerHTML = `
    ${COIN_SVG}
    <span class="gc-value">0</span>
  `;
  document.body.appendChild(root);

  const valueEl = root.querySelector<HTMLSpanElement>(".gc-value")!;

  // Format with thousands separator so 12345 reads "12,345" — matches the v9
  // mockup ("1,240"). Tabular-nums in the CSS keeps digit width fixed so the
  // counter doesn't jitter as numbers change.
  const fmt = new Intl.NumberFormat("en-US");

  let lastShown = -1;

  return {
    refresh(cube: Cube): void {
      const gold = (cube.resources?.gold ?? 0) | 0;  // resources undefined before first Colyseus delta
      if (gold === lastShown) return;
      lastShown = gold;
      valueEl.textContent = fmt.format(gold);
    },
    destroy(): void {
      root.remove();
    },
  };
}
